import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Shield, Clock, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";

const CyberSecurityLondon = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "4FOX Security - Cyber Security Services London",
    "description": "Cyber security services for London businesses including fractional CISO, risk management, and Cyber Essentials certification",
    "url": "https://4fox.uk/cyber-security-london",
    "areaServed": {
      "@type": "City",
      "name": "London",
      "containedIn": {
        "@type": "Country",
        "name": "United Kingdom"
      }
    },
    "serviceType": ["Cyber Security", "Information Security", "Fractional CISO", "vCISO Services"],
    "priceRange": "££-£££"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Security Services London | Fractional CISO & vCISO London | 4FOX Security"
        description="Expert cyber security services for London businesses. Fractional CISO, risk assessments, Cyber Essentials certification. Serving companies across Greater London with 24/7 emergency support."
        canonical="https://4fox.uk/cyber-security-london"
        keywords={[
          "cyber security London",
          "fractional CISO London",
          "vCISO London",
          "information security London",
          "cyber security consultant London",
          "Cyber Essentials London"
        ]}
        schema={localBusinessSchema}
        localAreas={["London", "Greater London", "City of London", "Westminster", "Camden"]}
      />
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Locations" },
              { label: "London" }
            ]}
          />

          {/* Hero Section */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-primary" />
              <span className="text-primary font-semibold">Serving London & Greater London</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Cyber Security Services in London
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl">
              Professional cyber security services for London businesses. From fractional CISO leadership 
              to 24/7 emergency incident response, we protect organisations across the capital.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <Card>
              <CardContent className="pt-6 text-center">
                <Shield className="w-12 h-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold mb-1">50+</div>
                <div className="text-sm text-muted-foreground">London Businesses Protected</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Clock className="w-12 h-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold mb-1">24/7</div>
                <div className="text-sm text-muted-foreground">Emergency Response</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Users className="w-12 h-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold mb-1">98%</div>
                <div className="text-sm text-muted-foreground">First-Time Pass Rate</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <MapPin className="w-12 h-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold mb-1">UK</div>
                <div className="text-sm text-muted-foreground">Based Team</div>
              </CardContent>
            </Card>
          </div>

          {/* Services for London */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Cyber Security Services for London Businesses</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">Fractional CISO Services</h3>
                  <p className="text-muted-foreground mb-4">
                    Strategic cyber security leadership for growing London businesses. Get C-level security 
                    expertise without the full-time cost. From £2,500/month.
                  </p>
                  <Button variant="outline" asChild>
                    <Link to="/services/fractional-ciso-uk">Learn More</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">24/7 Emergency Response</h3>
                  <p className="text-muted-foreground mb-4">
                    Immediate incident response for cyber emergencies. 15-minute callback guarantee for 
                    ransomware, data breaches, and security incidents.
                  </p>
                  <Button variant="outline" asChild>
                    <Link to="/services/24-hour-emergency-response">Learn More</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">Cyber Essentials Certification</h3>
                  <p className="text-muted-foreground mb-4">
                    Fast-track certification for London businesses. Essential for government contracts and 
                    supply chains. 4-6 week delivery with 98% pass rate.
                  </p>
                  <Button variant="outline" asChild>
                    <Link to="/services/cyber-essentials-certification-uk">Learn More</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">Risk Management</h3>
                  <p className="text-muted-foreground mb-4">
                    Comprehensive cyber risk assessments for London organisations. Identify, assess, and 
                    mitigate security risks to protect critical business assets.
                  </p>
                  <Button variant="outline" asChild>
                    <Link to="/services/risk-management">Learn More</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Why Choose Us for London */}
          <section className="mb-16 bg-muted/50 rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-8">Why London Businesses Choose 4FOX Security</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-3">London Market Expertise</h3>
                <p className="text-muted-foreground">
                  We understand the unique cyber security challenges facing London businesses, from financial 
                  services in the City to tech startups in Shoreditch and professional services in Canary Wharf.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3">UK Compliance Knowledge</h3>
                <p className="text-muted-foreground">
                  Deep expertise in UK and EU regulations including GDPR, Data Protection Act 2018, NIS 
                  Regulations, and industry-specific compliance requirements.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3">Rapid London Response</h3>
                <p className="text-muted-foreground">
                  On-site support available across London when needed. Our team can be at your London office 
                  for critical incidents, assessments, or strategic planning sessions.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3">Flexible Engagement Models</h3>
                <p className="text-muted-foreground">
                  From one-off projects to ongoing fractional CISO services. No long-term contracts. 
                  Scale security support as your London business grows.
                </p>
              </div>
            </div>
          </section>

          {/* Industries We Serve */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Industries We Serve in London</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                "Financial Services",
                "Professional Services",
                "Technology & SaaS",
                "Healthcare & Pharmaceuticals",
                "Legal Services",
                "Manufacturing",
                "Retail & E-commerce",
                "Media & Creative",
                "Education & Training"
              ].map((industry, index) => (
                <Card key={index}>
                  <CardContent className="pt-6 text-center">
                    <p className="font-semibold">{industry}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-primary text-primary-foreground rounded-lg p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Protect Your London Business Today</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Get expert cyber security support from a UK-based team that understands London business. 
              Free consultation available.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/contact">Book Free Consultation</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/assessment">Take Security Assessment</Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CyberSecurityLondon;
